/**
 * The off-canvas token/effect DOM mirror (phase1-plan §2; test-spec §6.2).
 *
 * PixiJS draws tokens as pixels; this renders the SAME token state as an
 * off-canvas, accessibility-tree DOM projection. It is two things at once:
 *   1. the screen-reader surface (a real list of tokens, stats and effects);
 *   2. the E2E assertion surface — E2E cannot read WebGL pixels, so it reads
 *      these `data-*` attributes instead (E2E-00 asserts they equal
 *      getState()).
 *
 * This module is the SINGLE source of the §6.2 machine-readable testids
 * (`token-list`, `stat`, `effect-chip`); the interactive inspector
 * deliberately does not duplicate them. Pure projection: no callbacks, no
 * mutation. Idempotent re-render.
 *
 * Companion to the seat mirror (`ui/seatPanel.ts`); together they are the
 * complete DOM mirror the plan calls for.
 */

import type { Effect, RoomState, StatKey, Token } from "@asarimitsuha/table-core";

const STAT_KEYS: readonly StatKey[] = ["hp", "shield", "mana", "rp"];

/** Effects grouped by the token they sit on, so each chip renders under its
 *  target in the tree (and still carries data-token-id for flat queries). */
function effectsByTarget(effects: Record<string, Effect>): Map<string, Effect[]> {
  const byTarget = new Map<string, Effect[]>();
  for (const effect of Object.values(effects)) {
    const list = byTarget.get(effect.targetTokenId);
    if (list) list.push(effect);
    else byTarget.set(effect.targetTokenId, [effect]);
  }
  // Deterministic chip order: by appliedAt then id (mirrors the engine's
  // simultaneous-expiry ordering in EFX-22, so the DOM never reshuffles).
  for (const list of byTarget.values()) {
    list.sort((a, b) => {
      const ra = a.appliedAt.round - b.appliedAt.round;
      if (ra !== 0) return ra;
      const ia = a.appliedAt.index - b.appliedAt.index;
      if (ia !== 0) return ia;
      return a.id.localeCompare(b.id);
    });
  }
  return byTarget;
}

function renderStat(token: Token, stat: StatKey): HTMLElement {
  const value = token.stats[stat];
  const el = document.createElement("span");
  el.dataset["testid"] = "stat";
  el.dataset["tokenId"] = token.id;
  el.dataset["stat"] = stat;
  el.dataset["value"] = String(value.current);
  el.dataset["max"] = String(value.max);
  el.textContent = `${stat.toUpperCase()} ${value.current}/${value.max}`;
  return el;
}

function renderEffectChip(effect: Effect): HTMLElement {
  const chip = document.createElement("span");
  chip.dataset["testid"] = "effect-chip";
  chip.dataset["effectId"] = effect.id;
  chip.dataset["tokenId"] = effect.targetTokenId;
  chip.dataset["name"] = effect.name;
  if (effect.expiry === "untilRemoved") {
    // No anchor/phase for a manual-removal effect; remaining is unbounded.
    chip.dataset["remaining"] = "∞";
  } else {
    chip.dataset["remaining"] = String(effect.expiry.roundsRemaining);
    chip.dataset["anchor"] = effect.expiry.anchorTokenId;
    chip.dataset["phase"] = effect.expiry.phase;
  }
  const remainingText =
    effect.expiry === "untilRemoved"
      ? "until removed"
      : `${effect.expiry.roundsRemaining}`;
  chip.textContent = `${effect.name} (${remainingText})`;
  return chip;
}

function renderTokenEntry(token: Token, effects: readonly Effect[]): HTMLElement {
  const item = document.createElement("li");
  item.dataset["tokenId"] = token.id;
  item.dataset["cell"] = `${token.col},${token.row}`;
  item.dataset["owner"] = token.ownerSeatId ?? "";
  item.dataset["name"] = token.name;

  const label = document.createElement("span");
  label.className = "token-name";
  label.textContent = token.name;
  item.appendChild(label);

  const stats = document.createElement("span");
  stats.className = "token-stats";
  for (const key of STAT_KEYS) stats.appendChild(renderStat(token, key));
  item.appendChild(stats);

  if (effects.length > 0) {
    const chips = document.createElement("span");
    chips.className = "token-effects-chips";
    for (const effect of effects) chips.appendChild(renderEffectChip(effect));
    item.appendChild(chips);
  }

  return item;
}

/**
 * Render the complete token mirror for `state` into `root`. Tokens are
 * emitted in a stable order (by id) so the DOM order never depends on object
 * iteration quirks. Idempotent.
 */
export function renderTokenMirror(root: HTMLElement, state: RoomState): void {
  root.replaceChildren();

  const list = document.createElement("ul");
  list.dataset["testid"] = "token-list";
  // Off-canvas but in the a11y tree; a label so a screen reader can find it.
  list.setAttribute("aria-label", "Tokens");

  const byTarget = effectsByTarget(state.effects);
  const tokens = Object.values(state.tokens).sort((a, b) =>
    a.id.localeCompare(b.id),
  );
  for (const token of tokens) {
    list.appendChild(renderTokenEntry(token, byTarget.get(token.id) ?? []));
  }
  root.appendChild(list);
}
